import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import LayoutContainer from 'components/Common/LayoutContainer'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl, IntlShape } from 'react-intl'
import styled from 'styled-components'
import Divider from 'LuxKit/Divider'
import { tierGradientBackgroundStyle, tierNormalContrastBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import useCurrentLoyaltyPendingPoints from 'loyalty/hooks/useCurrentLoyaltyPendingPoints'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Tooltip from 'LuxKit/Tooltip'
import CopyToClipboardClickableIcon from 'checkout/Components/Confirmation/BookingDetailsV2/CopyToClipboardClickableIcon'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import useLoyaltyAccount from 'loyalty/hooks/useLoyaltyAccount'
import LoyaltyAccountTileCircularProgressBar from 'loyalty/components/LoyaltyAccountTileCircularProgressBar'
import Caption from 'LuxKit/Typography/Caption'
import LoyaltyHeroUnlockTierCard from 'loyalty/components/LoyaltyHeroUnlockTierCard'
import useNavigateTo from 'hooks/useNavigateTo'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import useCurrentLoyaltyActivities from 'loyalty/hooks/useCurrentLoyaltyActivities'
import ModalContext from 'contexts/ModalContext'
import LoyaltyPointsDeductionInfoModal from 'loyalty/components/LoyaltyPointsDeductionInfoModal'
import { LOYALTY_ACCRUAL_DEDUCTION_CUT_OFF_DATE, LOYALTY_DISMISS_POINTS_DEDUCTION_MODAL_STORAGE_KEY } from 'loyalty/constants/loyaltyConfig'
import { get as getLocalStorage } from 'lib/storage/isomorphicLocalStorage'
import { dateIsAfterOrSame, dateIsBefore } from 'lib/datetime/dateUtils'
import { clamp } from 'lib/maths/mathUtils'
import { LUX_LOYALTY_POINTS_DEDUCTION_DATE } from '@luxuryescapes/lib-lux-loyalty'
import useOrders from 'hooks/Order/useOrders'
import { OptimizelyFeatureFlags } from 'constants/optimizely'

function getTimeBasedGreeting(intl: IntlShape): string {
  const hour = new Date().getHours()
  if (hour < 12) return intl.formatMessage({ id: 'ZyhCDANDWI', defaultMessage: 'Good morning', description: 'Time-based greeting for morning hours' })
  if (hour < 18) return intl.formatMessage({ id: 'RxTY12LMdi', defaultMessage: 'Good afternoon', description: 'Time-based greeting for afternoon hours' })
  return intl.formatMessage({ id: 'K1nt/xU8JY', defaultMessage: 'Good evening', description: 'Time-based greeting for evening hours' })
}

const HeroSection = styled.div`
  position: relative;
  padding: ${rem(32)} ${rem(16)};
  overflow: visible;
  z-index: 2;
  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}

  /* Hero uses darkContrast for platinum text (tierStyles uses normalContrast) */
  &.tier-platinum {
    color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
  }

  /* Custom 450 breakpoint to differentiate between mobile and tablet */
  @media (min-width: 450px) {
    padding: ${rem(40)} 0;
  }
`

const TierDivider = styled(Divider)`
  && {
    margin-top: auto;
    margin-bottom: auto;
    height: ${rem(48)};
    opacity: 0.5;

    @media (min-width: 450px) {
      height: ${rem(80)};
    }
  }
  ${tierNormalContrastBackgroundStyle}
`

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(16)};
  text-align: left;

  @media (min-width: 450px) {
    align-items: center;
  }

  ${mediaQueryUp.desktop} {
    display: grid;
    justify-content: stretch;
    align-items: start;
    gap: ${rem(24)};
    grid-template-columns: 1fr minmax(${rem(280)}, ${rem(383)});
  }

  ${mediaQueryUp.largeDesktop} {
    gap: ${rem(40)};
  }
`

const TierCardWrapper = styled.div`
  width: 100%;
  text-align: left;
  align-self: flex-start;
  margin-top: ${rem(12)};

  @media (min-width: 450px) {
    width: auto;
    max-width: ${rem(400)};
    margin-top: ${rem(16)};
    align-self: center;
  }

  ${mediaQueryUp.desktop} {
    width: 100%;
    max-width: none;
    min-width: 0;
    padding-top: ${rem(37)};
    margin-top: 0;
    align-self: auto;
  }
`

const LinkWrapper = styled.div`
  min-height: ${rem(24)};
`

const MembershipInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (min-width: 450px) {
    flex-direction: row;
    flex-wrap: wrap;
  }
`

const MembershipDot = styled(BodyText)`
  display: none;
  padding: 0 ${rem(8)};

  @media (min-width: 450px) {
    display: block;
  }
`

/* Progress bar item - vertical on mobile, horizontal on tablet+ */
const ProgressBarItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: ${rem(8)};

  @media (min-width: 450px) {
    flex-direction: row;
    gap: ${rem(10)};
    text-align: left;
  }
`

/* Left section wrapper */
const LeftSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(15)};
  width: 100%;

  @media (min-width: 450px) {
    gap: ${rem(20)};
    width: auto;
  }
`

const TopInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(6)};

  @media (min-width: 450px) {
    gap: ${rem(8)};
  }
`

const InfoIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  line-height: 1;
`

const PointsSection = styled.div`
  padding: ${rem(8)} 0 0 0;

  @media (min-width: 450px) {
    padding: ${rem(8)} 0;
  }
`

const ProgressCirclesWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  padding-left: ${rem(30)};
  padding-top: ${rem(8)};

  @media (min-width: 450px) {
    padding-left: 0;
    padding-top: 0;
  }
`

/* Container for the two progress bars */
const ProgressBarsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${rem(95)};

  @media (min-width: 450px) {
    gap: ${rem(55)};
  }
`

const FadedLoadingBox = styled(LoadingBox)`
  opacity: 0.3;
`

const FadedTextLoadingBox = styled(TextLoadingBox)`
  opacity: 0.3;
`

interface Props {
  activeTab: string
}

function LoyaltyAccountLandingPageHero(props: Props) {
  const intl = useIntl()
  const { activeTab } = props
  const [tier, tierName, nextTier] = useCurrentLoyaltyTier()
  const firstName = useAppSelector(state => state.auth.account.givenName)
  const account = useLoyaltyAccount().data!
  const [pendingPoints, pendingPointsFetching] = useCurrentLoyaltyPendingPoints()
  const [activities, activitiesFetching] = useCurrentLoyaltyActivities()
  const [userOrders, hasFetchedUserOrders] = useOrders()
  const navigateTo = useNavigateTo()
  const hasDismissedPointsDeductionModal = !!getLocalStorage(LOYALTY_DISMISS_POINTS_DEDUCTION_MODAL_STORAGE_KEY)
  const showModal = useContext(ModalContext)
  const hasLoyaltyPointsDeductionWarningEnabled = useAppSelector(state => !!state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.loyaltyPointsDeductionWarning])

  const hasManualPointsDeduction = useMemo<boolean>(() => {
    return activities.some(activity => activity.type === 'points_ratio_adjust')
  }, [activities])

  const hasPendingPointsDeduction = useMemo<boolean>(() => {
    return pendingPoints.items.some(item => dateIsBefore(new Date(item.createdAt), new Date(LUX_LOYALTY_POINTS_DEDUCTION_DATE)))
  }, [pendingPoints.items])

  const hasAccrualPointsDeduction = useMemo<boolean>(() => {
    return activities.some(activity => {
      if (activity.type !== 'earn_points') return false
      // Accrual happened after 27th June 2025 AEST
      if (!dateIsAfterOrSame(new Date(activity.date), new Date(LOYALTY_ACCRUAL_DEDUCTION_CUT_OFF_DATE))) return false
      const activityOrder = userOrders.find(order => order.id === activity.orderId)
      // Booking date is before the points deduction date
      return activityOrder && dateIsBefore(new Date(activityOrder.createdAt), new Date(LUX_LOYALTY_POINTS_DEDUCTION_DATE))
    })
  }, [activities, userOrders])

  const hasPointsDeduction =
    hasManualPointsDeduction ||
    (hasPendingPointsDeduction && hasLoyaltyPointsDeductionWarningEnabled) ||
    (hasAccrualPointsDeduction && hasLoyaltyPointsDeductionWarningEnabled)

  const handlePointsDeductionModal = useCallback(() => (
    showModal(<LoyaltyPointsDeductionInfoModal />)
  ), [showModal])

  const isFetching = activitiesFetching || pendingPointsFetching || !hasFetchedUserOrders

  const handleViewActivityClick = useCallback((event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    event.preventDefault()
    document.getElementById('loyalty-your-activity')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  // Handle view activity from 'about' tab - switches to benefits tab and scrolls to activity section
  const handleViewActivityFromAbout = useCallback((event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    event.preventDefault()
    navigateTo(`/${LOYALTY_LANDING_PAGE}#benefits`)
    setTimeout(() => {
      document.getElementById('loyalty-your-activity')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }, [navigateTo])

  const membershipNumber = `${account.accountNumber}`

  // only platinum tier has retain mode
  const isRetainMode = !nextTier
  const unlockCardTier = nextTier ?? tier
  const unlockCardCreditsRemaining = isRetainMode
    ? Math.max(account.totalCreditsForTierRetention - account.statusCredits, 0)
    : Math.max(account.totalCreditsForTierUpgrade - account.statusCredits, 0)
  const unlockCardNightsRemaining = isRetainMode
    ? Math.max(account.numberOfNightsForTierRetention - account.numberOfNights, 0)
    : Math.max(account.numberOfNightsForTierUpgrade - account.numberOfNights, 0)

  // Platinum user has retained their tier if they're in retain mode and have met all requirements
  const hasRetainedTier = isRetainMode && unlockCardCreditsRemaining === 0 && unlockCardNightsRemaining === 0

  // Calculate progress for status credits
  const statusCreditsProgress = useMemo(() => {
    const target = isRetainMode ? account.totalCreditsForTierRetention : account.totalCreditsForTierUpgrade
    if (target === 0) return 100
    const baseProgress = Math.floor((account.statusCredits / target) * 100)
    return clamp(0, baseProgress, 100)
  }, [account.statusCredits, account.totalCreditsForTierRetention, account.totalCreditsForTierUpgrade, isRetainMode])

  // Calculate progress for nights
  const nightsProgress = useMemo(() => {
    const target = isRetainMode ? account.numberOfNightsForTierRetention : account.numberOfNightsForTierUpgrade
    if (target === 0) return 100
    const baseProgress = Math.floor((account.numberOfNights / target) * 100)
    return clamp(0, baseProgress, 100)
  }, [account.numberOfNights, account.numberOfNightsForTierRetention, account.numberOfNightsForTierUpgrade, isRetainMode])

  const handleViewDetails = useCallback(() => {
    // Platinum tier anchors to "Your Platinum benefits" section, other tiers anchor to "Unlock [next tier]" section
    const anchor = isRetainMode ? 'benefits-your-tier' : 'benefits-your-benefits'
    const element = document.getElementById(anchor)

    if (element) {
      // Already on benefits tab - scroll immediately
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    } else {
      // If need to switch tabs - navigate and let useEffect in target component handle scroll
      navigateTo(`/${LOYALTY_LANDING_PAGE}#${anchor}`)
    }
  }, [navigateTo, isRetainMode])

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <HeroSection className={`tier-${tier}`}>
      <LayoutContainer variant="medium">
        <HeroContent>
          <LeftSectionContent>
            <TopInfoSection>
              <Group
                direction="horizontal"
                gap={12}
                horizontalAlign="start"
                verticalAlign="center"
              >
                <LoyaltyProgramNameLogo width={104} />
                <LoyaltyTierLabel kind="primary" tier={tier} format="uppercase">{tierName}</LoyaltyTierLabel>
              </Group>

              {/* Greeting based on local time */}
              <Heading variant="heading2">
                {getTimeBasedGreeting(intl)}{firstName ? `, ${firstName}` : ''}
              </Heading>

              <MembershipInfo>
                <Group direction="horizontal" gap={4} verticalAlign="center">
                  <BodyText variant="medium">
                    {intl.formatMessage({ id: '9BU6E0mGcL', defaultMessage: 'Membership {membershipNumber}', description: 'Label showing user membership number' }, { membershipNumber })}
                  </BodyText>
                  <CopyToClipboardClickableIcon
                    value={membershipNumber}
                    ariaLabel={intl.formatMessage({ id: 'fg53uIyhkd', defaultMessage: 'Copy membership number', description: 'Aria label for copy membership number button' })}
                  />
                </Group>
                {account.statusReviewDate && (
                  <>
                    <MembershipDot variant="medium">·</MembershipDot>
                    <BodyText variant="medium">
                      {intl.formatMessage({ id: 'lvxaMpVjBD', defaultMessage: 'Review date', description: 'Label for membership status review date' })}{' '}<FormatDateTime value={account.statusReviewDate} format="dmy-casual-short" />
                    </BodyText>
                  </>
                )}
              </MembershipInfo>
            </TopInfoSection>

            {/* Points summary */}
            <PointsSection>
              {isFetching && (
                <Group direction="horizontal" gap={8} verticalAlign="center">
                  <FadedTextLoadingBox width="6ch" typography="heading3" />
                  <FadedTextLoadingBox width="6ch" typography="body-medium" />
                </Group>
              )}
              {!isFetching && (
                <Group direction="vertical" gap={4}>
                  <Group direction="horizontal" gap={16} verticalAlign="center">
                    <Group direction="horizontal" gap={4} verticalAlign="center">
                      <Heading variant="heading3">
                        <FormatDecimal value={account.points} />
                      </Heading>
                      <BodyText variant="medium" weight="bold">
                        {intl.formatMessage({ id: 'zkLkoM48HK', defaultMessage: 'points', description: 'Label for loyalty points' })}
                      </BodyText>
                    </Group>
                    <Group direction="horizontal" gap={4} verticalAlign="center">
                      <BodyText variant="medium">
                        <FormatDecimal value={pendingPoints.points} /> {intl.formatMessage({ id: 'g9MUmPOY0y', defaultMessage: 'pending', description: 'Label for pending points' })}
                      </BodyText>
                      <Tooltip description={intl.formatMessage({ id: 'qA4RV/KMDo', defaultMessage: 'Pending points may take up to 24hrs to appear', description: 'Tooltip explaining pending points delay' })} placement="bottom">
                        <InfoIconWrapper><LineInfoCircleIcon size={16} /></InfoIconWrapper>
                      </Tooltip>
                    </Group>
                  </Group>
                  {hasPointsDeduction && !hasDismissedPointsDeductionModal && (
                    <TextLink
                      size="small"
                      weight="regular"
                      underline
                      onClick={handlePointsDeductionModal}
                    >
                      {intl.formatMessage({ id: 'tc3DdjKeS7', defaultMessage: 'What\'s changed', description: 'Link text to view what has changed with points deduction' })}
                    </TextLink>
                  )}
                </Group>
              )}
            </PointsSection>

            <ProgressCirclesWrapper>
              {isFetching && (
                <ProgressBarsContainer>
                  <ProgressBarItem>
                    <FadedLoadingBox width={76} height={76} />
                    <div>
                      <FadedTextLoadingBox width="12ch" typography="body-small" />
                      <FadedTextLoadingBox width="8ch" typography="body-small" />
                    </div>
                  </ProgressBarItem>
                  <ProgressBarItem>
                    <FadedLoadingBox width={76} height={76} />
                    <div>
                      <FadedTextLoadingBox width="6ch" typography="body-small" />
                      <FadedTextLoadingBox width="8ch" typography="body-small" />
                    </div>
                  </ProgressBarItem>
                </ProgressBarsContainer>
              )}
              {!isFetching && (
                <ProgressBarsContainer>
                  <ProgressBarItem>
                    <LoyaltyAccountTileCircularProgressBar
                      progress={statusCreditsProgress}
                      tier={tier}
                    >
                      <Group direction="vertical" horizontalAlign="center" gap={2}>
                        <BodyText variant="medium" weight="bold">
                          <FormatDecimal value={account.statusCredits} />
                        </BodyText>
                        <TierDivider className={`tier-${tier}`} kind="primary" />
                        <Caption variant="small">
                          <FormatDecimal value={isRetainMode ? account.totalCreditsForTierRetention : account.totalCreditsForTierUpgrade} />
                        </Caption>
                      </Group>
                    </LoyaltyAccountTileCircularProgressBar>

                    <Group direction="vertical" gap={4}>
                      <BodyText variant="small" weight="bold">{intl.formatMessage({ id: 'nwj+muZ0Fj', defaultMessage: 'Status Credits', description: 'Label for status credits' })}</BodyText>
                      <Group direction="horizontal" gap={4} verticalAlign="center">
                        <BodyText variant="small"><FormatDecimal value={pendingPoints.statusCredits} /> {intl.formatMessage({ id: 'KCRfAfHZYl', defaultMessage: 'pending', description: 'Label for pending status credits' })}</BodyText>
                        <Tooltip description={intl.formatMessage({ id: 'lzJ4GCY8iM', defaultMessage: 'Pending status credits may take up to 24hrs to appear', description: 'Tooltip explaining pending status credits delay' })} placement="bottom">
                          <InfoIconWrapper><LineInfoCircleIcon size={16} /></InfoIconWrapper>
                        </Tooltip>
                      </Group>
                    </Group>
                  </ProgressBarItem>

                  <ProgressBarItem>
                    <LoyaltyAccountTileCircularProgressBar
                      progress={nightsProgress}
                      tier={tier}
                    >
                      <Group direction="vertical" horizontalAlign="center" gap={2}>
                        <BodyText variant="medium" weight="bold">
                          <FormatDecimal value={account.numberOfNights} />
                        </BodyText>
                        <TierDivider className={`tier-${tier}`} kind="primary" />
                        <Caption variant="small">
                          <FormatDecimal value={isRetainMode ? account.numberOfNightsForTierRetention : account.numberOfNightsForTierUpgrade} />
                        </Caption>
                      </Group>
                    </LoyaltyAccountTileCircularProgressBar>

                    <Group direction="vertical" gap={4}>
                      <BodyText variant="small" weight="bold">{intl.formatMessage({ id: 'XYRqW7Vwth', defaultMessage: 'Nights', description: 'Label for loyalty nights' })}</BodyText>
                      <Group direction="horizontal" gap={4} verticalAlign="center">
                        <BodyText variant="small"><FormatDecimal value={pendingPoints.items.reduce((acc, item) => acc + (item.numNights ?? 0), 0)} /> {intl.formatMessage({ id: 'XN1cjITyzi', defaultMessage: 'pending', description: 'Label for pending nights' })}</BodyText>
                        <Tooltip description={intl.formatMessage({ id: 'vre18993PB', defaultMessage: 'Pending nights may take up to 24hrs to appear', description: 'Tooltip explaining pending nights delay' })} placement="bottom">
                          <InfoIconWrapper><LineInfoCircleIcon size={16} /></InfoIconWrapper>
                        </Tooltip>
                      </Group>
                    </Group>
                  </ProgressBarItem>
                </ProgressBarsContainer>
              )}
            </ProgressCirclesWrapper>

            {/* Show View activity link for desktop size */}
            <CSSBreakpoint min="desktop">
              <LinkWrapper>
                {activeTab === 'about' && <TextLink
                  size="medium"
                  block
                  href="#benefits"
                  onClick={handleViewActivityFromAbout}
                >
                  {intl.formatMessage({ id: 'nJNvCiJKIw', defaultMessage: 'View activity', description: 'Link to view loyalty activity from about tab' })}
                </TextLink>}
                {activeTab === 'benefits' && <TextLink
                  size="medium"
                  block
                  href="#loyalty-your-activity"
                  onClick={handleViewActivityClick}
                >
                  {intl.formatMessage({ id: 'h3fID5Zt28', defaultMessage: 'View activity', description: 'Link to view loyalty activity from benefits tab' })}
                </TextLink>}
              </LinkWrapper>
            </CSSBreakpoint>
          </LeftSectionContent>
          {/* Right Section - tier card */}
          {unlockCardTier && (
            <TierCardWrapper>
              <LoyaltyHeroUnlockTierCard
                tier={unlockCardTier}
                isRetain={isRetainMode}
                hasRetainedTier={hasRetainedTier}
                creditsRemaining={unlockCardCreditsRemaining}
                nightsRemaining={unlockCardNightsRemaining}
                expiryDate={account.statusReviewDate}
                onViewDetails={handleViewDetails}
              />
            </TierCardWrapper>
          )}
        </HeroContent>
      </LayoutContainer>

    </HeroSection>
  </ProductPaletteProvider>
}

export default LoyaltyAccountLandingPageHero
